import cx from 'classnames';
import { useContext } from 'react';
import {
  useShowSkeleton,
  useHasFinanceFailed,
  usePriceTerms
} from '@/SearchResults/ResultTile/hooks';
import PricingErrorTooltip from '@/SearchResults/ResultTileV2/Terms/PricingErrorTooltip';
import { isProd } from '~/constants/environment';
import { useHasSoftPull } from '~/contexts/FinanceAndTradeContext/FinanceAndTradeContext';
import { useShouldUpdateTerms } from '~/contexts/PricingContext';
import SearchContext from '~/contexts/SearchContext';
import { useHasActiveTradeApplied } from '~/hooks/selectors';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import MonthlyPayment from './MonthlyPayment';
import LoadingTerms from '../../LoadingTerms';

export default function Terms(): JSX.Element | null {
  const { pricingErrorMessage, totalCashDue = 0 } = usePriceTerms();
  const hasIndividualFinanceFailed = useHasFinanceFailed();
  const showSkeleton = useShowSkeleton();
  const hasActiveTradeApplied = useHasActiveTradeApplied();
  const hasValidTerms = useHasSoftPull();
  const { showFinance } = useContext(SearchContext);
  const termsAreUpdating = useShouldUpdateTerms();

  if (showSkeleton) {
    return (
      <div data-qa="base-terms" className="flex flex-col h-full justify-end">
        <div className="flex justify-end">
          <div className="bg-grey-1 h-16 overflow-hidden w-[70%]" />
        </div>
        <div className="flex justify-end">
          <div className="bg-grey-1 h-16 mt-4 overflow-hidden w-[70%]" />
        </div>
      </div>
    );
  }

  if (hasIndividualFinanceFailed) {
    return null;
  }

  if (pricingErrorMessage && !isProd) {
    return (
      <div
        data-qa="base-terms"
        className={cx('flex mb-20 mr-16 self-end text-sm z-100', 'bold', {
          hidden: !showFinance
        })}
      >
        <PricingErrorTooltip pricingErrorMessage={pricingErrorMessage} />
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex flex-col h-full items-end justify-end text-grey-5 text-sm',
        {
          hidden: !showFinance
        }
      )}
      data-qa="base-terms"
    >
      {termsAreUpdating ? (
        <LoadingTerms />
      ) : (
        <>
          <MonthlyPayment hasValidTerms={hasValidTerms} />
          <div
            data-down-payment={totalCashDue}
            className="whitespace-nowrap"
            data-qa="down-payment"
          >
            ${formatAndDropDecimal(totalCashDue)} cash down
          </div>
          {hasActiveTradeApplied && (
            <div className="trade-in">with trade-in</div>
          )}
        </>
      )}
    </div>
  );
}
